import type { AxiosInstance, AxiosResponse } from 'axios';
import type { SortField } from '@/models/common';
import type { SurveyResponse } from '@/models/response';
import type { RootResponse } from '@/models/rootResponse';

export type GetSurveyResponsesMeta = {
   surveyId: string;
};

export type SurveyResponsesMeta = {
   entries_count: number;
   reviewed_entries_count: number;
   has_file_question: boolean;
   has_participant_block: boolean;
   id: string;
};

export type GetInfiniteSurveyResponses = {
   surveyId: string | undefined;
   page?: number;
   sorts?: SortField[];
   search?: string;
   statuses?: string[];
};

export type GetCurrentFirstResponse = {
   surveyId: string;
};

export type GetSurveyResponseAttachments = {
   surveyId: string;
   isGetAll: boolean;
   responseIds: string[];
};

export type GetSurveyResponseCsv = {
   isGetAll: boolean;
   responseIds: string[];
   timezone: string;
   surveyId: string;
};

type StartTimer = {
   entryId: string;
   timestamp: string;
};

export type GetEntry = {
   entryId: string;
   token: string;
};

class SurveyResponsesService {
   private provider: AxiosInstance;

   constructor(provider: AxiosInstance) {
      this.provider = provider;
   }

   async getCurrentFirstResponse({
      surveyId,
   }: GetCurrentFirstResponse): Promise<RootResponse<SurveyResponse[]>> {
      const response: AxiosResponse<RootResponse<SurveyResponse[]>> =
         await this.provider.post(`/surveys/${surveyId}/entries/search`, {
            includes: [
               {
                  relation: 'answers.media',
                  filters: [
                     {
                        field: 'answers.media.collection_name',
                        operator: '=',
                        value: 'file',
                     },
                  ],
               },
               { relation: 'answers.answerable' },
               { relation: 'guestSessions.guest' },
               { relation: 'guestSessions.guest.questions' },
               { relation: 'guestSessions.guest.questionChoices' },
               { relation: 'survey' },
               { relation: 'guestSessions.session' },
               { relation: 'guestSessions.guest.media' },
            ],
            sort: [{ field: 'created_at', direction: 'asc' }],
         });
      return response.data;
   }

   async getSurveyResponse(
      entryId: string,
   ): Promise<RootResponse<SurveyResponse[]>> {
      const response: AxiosResponse<RootResponse<SurveyResponse[]>> =
         await this.provider.post(`rest/answers/search`, {
            search: {
               filters: [{ field: 'entry_id', value: entryId }],
               includes: [{ relation: 'media' }],
            },
         });

      return response.data;
   }

   async getInfiniteSurveyResponses({
      surveyId,
      page,
      sorts,
      search,
      statuses,
   }: GetInfiniteSurveyResponses): Promise<RootResponse<SurveyResponse[]>> {
      const response: AxiosResponse<RootResponse<SurveyResponse[]>> =
         await this.provider.post(`rest/entries/search`, {
            search: {
               text: {
                  value: '',
               },

               page,
               gates: ['delete'],
               filters: [
                  {
                     field: 'survey_id',
                     value: surveyId,
                  },
                  ...(statuses
                     ? [
                          {
                             field: 'status.slug',
                             operator: 'in',
                             value: statuses,
                          },
                       ]
                     : []),
               ],
               ...(sorts?.length
                  ? { sorts }
                  : {
                       sorts: [{ field: 'created_at', direction: 'desc' }],
                    }),
               includes: [
                  {
                     relation: 'guestSessions',
                     includes: [
                        { relation: 'session' },
                        {
                           relation: 'guest',
                           includes: [
                              { relation: 'questions' },
                              { relation: 'questionChoices' },
                              { relation: 'media' },
                           ],
                        },
                     ],
                  },
                  {
                     relation: 'status',
                     selects: [{ field: 'slug' }],
                  },
               ],
               instructions: [
                  {
                     name: 'search-guest-in-entry',
                     fields: [
                        {
                           name: 'search',
                           value: search,
                        },
                     ],
                  },
               ],
            },
         });

      return response.data;
   }

   async getSurveyResponsesMeta({
      surveyId,
   }: GetSurveyResponsesMeta): Promise<RootResponse<SurveyResponsesMeta[]>> {
      const response: AxiosResponse<RootResponse<SurveyResponsesMeta[]>> =
         await this.provider.post(`rest/surveys/search`, {
            search: {
               filters: [{ field: 'id', operator: '=', value: surveyId }],
               aggregates: [
                  {
                     relation: 'entries',
                     type: 'count',
                     alias: 'entries_count',
                  },
                  {
                     relation: 'entries',
                     type: 'count',
                     alias: 'reviewed_entries_count',
                     filters: [
                        {
                           field: 'status.slug',
                           operator: '=',
                           value: 'reviewed',
                        },
                     ],
                  },
               ],
            },
         });

      return {
         data: response.data.data,
         meta: response.data.meta,
      };
   }

   async getSurveyResponseAttachments({
      surveyId,
      isGetAll,
      responseIds,
   }: GetSurveyResponseAttachments): Promise<ArrayBuffer> {
      const response: AxiosResponse<ArrayBuffer> = await this.provider.post(
         `rest/surveys/${surveyId}/entries/export-zip-files`,
         { entries: responseIds, all: isGetAll },
         { responseType: 'arraybuffer' },
      );

      return response.data;
   }

   async exportZipByEntryId(entryId: string): Promise<ArrayBuffer> {
      const response: AxiosResponse<ArrayBuffer> = await this.provider.get(
         `/entries/${entryId}/answers/exportZip`,
         { responseType: 'arraybuffer' },
      );
      return response.data;
   }

   async exportCsvBySurveyId(
      surveyId: string,
      timezone: string,
   ): Promise<ArrayBuffer> {
      const response: AxiosResponse<ArrayBuffer> = await this.provider.get(
         `/surveys/${surveyId}/answers/export?tz=${timezone}`,
         { responseType: 'arraybuffer' },
      );
      return response.data;
   }

   async requestSurveyResponseSimpleExport({
      isGetAll,
      responseIds,
      timezone,
      surveyId,
   }: GetSurveyResponseCsv): Promise<void> {
      await this.provider.post(
         `rest/surveys/${surveyId}/entries/simple-export`,
         { entries: responseIds, all: isGetAll, timezone },
      );
   }

   async requestSurveyResponseDetailedExport({
      isGetAll,
      responseIds,
      timezone,
      surveyId,
   }: GetSurveyResponseCsv): Promise<void> {
      await this.provider.post(
         `rest/surveys/${surveyId}/entries/detailed-export`,
         { entries: responseIds, all: isGetAll, timezone },
      );
   }

   async downloadGeneratedExport(downloadUrl: string): Promise<ArrayBuffer> {
      const response: AxiosResponse<ArrayBuffer> = await this.provider.get(
         downloadUrl,
         { responseType: 'arraybuffer' },
      );

      return response.data;
   }

   async deleteResponse(entryIds: string[]): Promise<void> {
      await this.provider.delete('rest/entries', {
         data: { resources: entryIds },
      });
   }

   async deleteAllResponses(surveyId: string): Promise<void> {
      await this.provider.post('/rest/entries/actions/delete-all', {
         fields: [{ name: 'survey_id', value: surveyId }],
      });
   }

   async startTimer({ entryId, timestamp }: StartTimer) {
      await this.provider.post(`/entries/${entryId}/start`, {
         started_at: timestamp,
      });
   }

   async getEntryWithToken({
      entryId,
      token,
   }: GetEntry): Promise<RootResponse<[SurveyResponse]>> {
      const response = await this.provider.get(`/rest/entries/${entryId}`, {
         headers: { 'X-Entry-Token': token },
      });

      return response.data;
   }
}

export default SurveyResponsesService;
